/**
 * Cost Calculation Service - Fleet Premium
 *
 * Servico para calculo de custos de roteirizacao:
 * - Combustivel (consumo km/L x preco/litro)
 * - Pedagios (manual ou API)
 * - Motorista (hora trabalhada)
 * - Manutencao (custo por km)
 * - Outros custos
 */

// =======================================
// Constantes e Configuracoes Padrao
// =======================================

/**
 * Configuracoes padrao de custos (valores medios Brasil 2026)
 */
export const DEFAULT_COST_CONFIG = {
  fuel: {
    consumptionKmL: 10, // km por litro (medio carro popular)
    pricePerLiter: 6.5, // R$ por litro (gasolina)
    fuelType: 'gasoline', // gasoline, ethanol, diesel
  },
  driver: {
    hourlyRate: 25, // R$ por hora
    overtimeMultiplier: 1.5, // multiplicador hora extra
    dailyLimit: 8, // horas normais por dia
  },
  maintenance: {
    costPerKm: 0.15, // R$ por km (depreciacao, pneus, revisao)
    enabled: true,
  },
  tolls: {
    enabled: true,
    estimatePerKm: 0.1, // R$ por km (estimativa media)
    manualEntries: [], // pedagios manuais
  },
  other: {
    insurancePerDay: 50, // seguro por dia
    parkingEstimate: 20, // estacionamento estimado por rota
  },
};

/**
 * Tipos de combustivel com fatores de ajuste
 */
export const FUEL_TYPES = {
  gasoline: { label: 'Gasolina', factor: 1.0, avgPrice: 6.5 },
  ethanol: { label: 'Etanol', factor: 0.7, avgPrice: 4.5 }, // 70% eficiencia vs gasolina
  diesel: { label: 'Diesel', factor: 1.3, avgPrice: 6.0 }, // 30% mais eficiente
  gnv: { label: 'GNV', factor: 0.9, avgPrice: 4.0 },
  electric: { label: 'Eletrico', factor: 0, avgPrice: 0 }, // custo por kWh diferente
};

/**
 * Perfis de veiculos com configuracoes tipicas
 */
export const VEHICLE_PROFILES = {
  motorcycle: {
    label: 'Moto',
    consumptionKmL: 35,
    maintenancePerKm: 0.08,
    fuelType: 'gasoline',
  },
  car_small: {
    label: 'Carro Pequeno',
    consumptionKmL: 12,
    maintenancePerKm: 0.12,
    fuelType: 'gasoline',
  },
  car_medium: {
    label: 'Carro Medio',
    consumptionKmL: 10,
    maintenancePerKm: 0.15,
    fuelType: 'gasoline',
  },
  van: {
    label: 'Van/Furgao',
    consumptionKmL: 8,
    maintenancePerKm: 0.20,
    fuelType: 'diesel',
  },
  truck_small: {
    label: 'Caminhao Leve (3/4)',
    consumptionKmL: 6,
    maintenancePerKm: 0.30,
    fuelType: 'diesel',
  },
  truck_medium: {
    label: 'Caminhao Medio (Toco)',
    consumptionKmL: 4,
    maintenancePerKm: 0.45,
    fuelType: 'diesel',
  },
  truck_heavy: {
    label: 'Caminhao Pesado (Truck)',
    consumptionKmL: 2.5,
    maintenancePerKm: 0.60,
    fuelType: 'diesel',
  },
};

// =======================================
// Funcoes de Calculo
// =======================================

/**
 * Calcula custo de combustivel
 * @param {number} distanceKm - Distancia em km
 * @param {object} fuelConfig - Configuracao de combustivel
 * @returns {object} Detalhes do custo de combustivel
 */
export const calculateFuelCost = (distanceKm, fuelConfig = DEFAULT_COST_CONFIG.fuel) => {
  const { consumptionKmL, pricePerLiter, fuelType } = fuelConfig;

  // Ajustar consumo pelo tipo de combustivel
  const fuelFactor = FUEL_TYPES[fuelType]?.factor || 1.0;
  const adjustedConsumption = consumptionKmL * fuelFactor;

  // Calcular litros necessarios
  const litersNeeded = distanceKm / adjustedConsumption;

  // Calcular custo total
  const cost = litersNeeded * pricePerLiter;

  return {
    distanceKm,
    consumptionKmL: adjustedConsumption,
    litersNeeded: Math.round(litersNeeded * 100) / 100,
    pricePerLiter,
    fuelType,
    cost: Math.round(cost * 100) / 100,
  };
};

/**
 * Calcula custo do motorista
 * @param {number} durationSeconds - Duracao em segundos
 * @param {object} driverConfig - Configuracao do motorista
 * @returns {object} Detalhes do custo do motorista
 */
export const calculateDriverCost = (durationSeconds, driverConfig = DEFAULT_COST_CONFIG.driver) => {
  const { hourlyRate, overtimeMultiplier, dailyLimit } = driverConfig;

  const totalHours = durationSeconds / 3600;
  const regularHours = Math.min(totalHours, dailyLimit);
  const overtimeHours = Math.max(0, totalHours - dailyLimit);

  const regularCost = regularHours * hourlyRate;
  const overtimeCost = overtimeHours * hourlyRate * overtimeMultiplier;
  const totalCost = regularCost + overtimeCost;

  return {
    totalHours: Math.round(totalHours * 100) / 100,
    regularHours: Math.round(regularHours * 100) / 100,
    overtimeHours: Math.round(overtimeHours * 100) / 100,
    hourlyRate,
    overtimeMultiplier,
    regularCost: Math.round(regularCost * 100) / 100,
    overtimeCost: Math.round(overtimeCost * 100) / 100,
    cost: Math.round(totalCost * 100) / 100,
  };
};

/**
 * Calcula custo de manutencao
 * @param {number} distanceKm - Distancia em km
 * @param {object} maintenanceConfig - Configuracao de manutencao
 * @returns {object} Detalhes do custo de manutencao
 */
export const calculateMaintenanceCost = (distanceKm, maintenanceConfig = DEFAULT_COST_CONFIG.maintenance) => {
  const { costPerKm, enabled } = maintenanceConfig;

  if (!enabled) {
    return { distanceKm, costPerKm: 0, cost: 0, enabled: false };
  }

  const cost = distanceKm * costPerKm;

  return {
    distanceKm,
    costPerKm,
    cost: Math.round(cost * 100) / 100,
    enabled: true,
  };
};

/**
 * Calcula custo de pedagios
 * @param {number} distanceKm - Distancia em km
 * @param {object} tollConfig - Configuracao de pedagios
 * @returns {object} Detalhes do custo de pedagios
 */
export const calculateTollCost = (distanceKm, tollConfig = DEFAULT_COST_CONFIG.tolls) => {
  const { enabled, estimatePerKm, manualEntries } = tollConfig;

  if (!enabled) {
    return {
      distanceKm,
      estimatedCost: 0,
      manualCost: 0,
      cost: 0,
      enabled: false,
    };
  }

  // Custo estimado por distancia
  const estimatedCost = distanceKm * estimatePerKm;

  // Custo manual (pedagios especificos)
  const manualCost = (manualEntries || []).reduce((sum, entry) => sum + (entry.value || 0), 0);

  // Se houver pedagios manuais, usar eles; senao, usar estimativa
  const totalCost = manualEntries?.length > 0 ? manualCost : estimatedCost;

  return {
    distanceKm,
    estimatePerKm,
    estimatedCost: Math.round(estimatedCost * 100) / 100,
    manualEntries: manualEntries || [],
    manualCost: Math.round(manualCost * 100) / 100,
    cost: Math.round(totalCost * 100) / 100,
    enabled: true,
    isEstimated: !(manualEntries?.length > 0),
  };
};

/**
 * Calcula outros custos (seguro, estacionamento, etc)
 * @param {object} otherConfig - Configuracao de outros custos
 * @param {number} daysWorked - Numero de dias trabalhados
 * @param {number} stopsCount - Numero de paradas
 * @returns {object} Detalhes de outros custos
 */
export const calculateOtherCosts = (
  otherConfig = DEFAULT_COST_CONFIG.other,
  daysWorked = 1,
  stopsCount = 0
) => {
  const { insurancePerDay, parkingEstimate } = otherConfig;

  const insuranceCost = insurancePerDay * daysWorked;
  const parkingCost = parkingEstimate; // Por rota, nao por parada
  const totalCost = insuranceCost + parkingCost;

  return {
    insurancePerDay,
    insuranceCost: Math.round(insuranceCost * 100) / 100,
    parkingEstimate,
    parkingCost: Math.round(parkingCost * 100) / 100,
    daysWorked,
    stopsCount,
    cost: Math.round(totalCost * 100) / 100,
  };
};

/**
 * Calcula custo total de uma rota
 * @param {object} route - Dados da rota
 * @param {object} costConfig - Configuracao de custos
 * @returns {object} Resumo completo de custos
 */
export const calculateRouteCost = (route, costConfig = DEFAULT_COST_CONFIG) => {
  const distanceKm = (route.distance || 0) / 1000; // metros para km
  const durationSeconds = route.duration || 0;
  const stopsCount = route.steps?.filter((s) => s.type === 'job').length || 0;

  // Calcular cada componente
  const fuel = calculateFuelCost(distanceKm, costConfig.fuel);
  const driver = calculateDriverCost(durationSeconds, costConfig.driver);
  const maintenance = calculateMaintenanceCost(distanceKm, costConfig.maintenance);
  const tolls = calculateTollCost(distanceKm, costConfig.tolls);
  const other = calculateOtherCosts(costConfig.other, 1, stopsCount);

  // Total
  const totalCost = fuel.cost + driver.cost + maintenance.cost + tolls.cost + other.cost;

  // Custo por km e por parada
  const costPerKm = distanceKm > 0 ? totalCost / distanceKm : 0;
  const costPerStop = stopsCount > 0 ? totalCost / stopsCount : 0;

  return {
    route: {
      distanceKm: Math.round(distanceKm * 100) / 100,
      durationSeconds,
      durationHours: Math.round((durationSeconds / 3600) * 100) / 100,
      stopsCount,
    },
    breakdown: {
      fuel,
      driver,
      maintenance,
      tolls,
      other,
    },
    summary: {
      fuelCost: fuel.cost,
      driverCost: driver.cost,
      maintenanceCost: maintenance.cost,
      tollsCost: tolls.cost,
      otherCosts: other.cost,
      totalCost: Math.round(totalCost * 100) / 100,
      costPerKm: Math.round(costPerKm * 100) / 100,
      costPerStop: Math.round(costPerStop * 100) / 100,
    },
  };
};

/**
 * Calcula custo total de um plano (multiplas rotas)
 * @param {array} routes - Array de rotas
 * @param {object} costConfig - Configuracao de custos
 * @returns {object} Resumo de custos do plano
 */
export const calculatePlanCost = (routes, costConfig = DEFAULT_COST_CONFIG) => {
  if (!routes || routes.length === 0) {
    return {
      routes: [],
      totals: {
        distanceKm: 0,
        durationHours: 0,
        stopsCount: 0,
        vehiclesCount: 0,
        fuelCost: 0,
        driverCost: 0,
        maintenanceCost: 0,
        tollsCost: 0,
        otherCosts: 0,
        totalCost: 0,
      },
    };
  }

  // Calcular custo de cada rota
  const routeCosts = routes.map((route, index) => {
    const cost = calculateRouteCost(route, costConfig);
    return {
      routeIndex: index,
      vehicleId: route.vehicleId,
      ...cost,
    };
  });

  // Somar totais
  const totals = routeCosts.reduce(
    (acc, rc) => ({
      distanceKm: acc.distanceKm + rc.route.distanceKm,
      durationHours: acc.durationHours + rc.route.durationHours,
      stopsCount: acc.stopsCount + rc.route.stopsCount,
      fuelCost: acc.fuelCost + rc.summary.fuelCost,
      driverCost: acc.driverCost + rc.summary.driverCost,
      maintenanceCost: acc.maintenanceCost + rc.summary.maintenanceCost,
      tollsCost: acc.tollsCost + rc.summary.tollsCost,
      otherCosts: acc.otherCosts + rc.summary.otherCosts,
      totalCost: acc.totalCost + rc.summary.totalCost,
    }),
    {
      distanceKm: 0,
      durationHours: 0,
      stopsCount: 0,
      fuelCost: 0,
      driverCost: 0,
      maintenanceCost: 0,
      tollsCost: 0,
      otherCosts: 0,
      totalCost: 0,
    }
  );

  // Arredondar totais
  Object.keys(totals).forEach((key) => {
    totals[key] = Math.round(totals[key] * 100) / 100;
  });

  // Adicionar contagem de veiculos
  totals.vehiclesCount = routes.length;

  // Custo por km e por parada (totais)
  totals.costPerKm = totals.distanceKm > 0
    ? Math.round((totals.totalCost / totals.distanceKm) * 100) / 100
    : 0;
  totals.costPerStop = totals.stopsCount > 0
    ? Math.round((totals.totalCost / totals.stopsCount) * 100) / 100
    : 0;

  return {
    routes: routeCosts,
    totals,
    config: costConfig,
  };
};

// =======================================
// Funcoes de Comparacao
// =======================================

/**
 * Compara custos entre duas configuracoes ou rotas
 * @param {object} cost1 - Primeiro calculo de custo
 * @param {object} cost2 - Segundo calculo de custo
 * @returns {object} Comparacao detalhada
 */
export const compareCosts = (cost1, cost2) => {
  const diff = (a, b) => {
    const difference = b - a;
    const percentChange = a !== 0 ? ((difference / a) * 100) : 0;
    return {
      original: a,
      new: b,
      difference: Math.round(difference * 100) / 100,
      percentChange: Math.round(percentChange * 10) / 10,
      improved: difference < 0,
    };
  };

  return {
    totalCost: diff(cost1.summary?.totalCost || 0, cost2.summary?.totalCost || 0),
    fuelCost: diff(cost1.summary?.fuelCost || 0, cost2.summary?.fuelCost || 0),
    driverCost: diff(cost1.summary?.driverCost || 0, cost2.summary?.driverCost || 0),
    maintenanceCost: diff(cost1.summary?.maintenanceCost || 0, cost2.summary?.maintenanceCost || 0),
    tollsCost: diff(cost1.summary?.tollsCost || 0, cost2.summary?.tollsCost || 0),
    distanceKm: diff(cost1.route?.distanceKm || 0, cost2.route?.distanceKm || 0),
    durationHours: diff(cost1.route?.durationHours || 0, cost2.route?.durationHours || 0),
  };
};

// =======================================
// Persistencia de Configuracao
// =======================================

const COST_CONFIG_STORAGE_KEY = 'fleet_premium_cost_config';

/**
 * Salva configuracao de custos no localStorage
 * @param {object} config - Configuracao a salvar
 */
export const saveCostConfig = (config) => {
  try {
    localStorage.setItem(COST_CONFIG_STORAGE_KEY, JSON.stringify(config));
    return true;
  } catch (error) {
    console.error('[CostConfig] Error saving:', error);
    return false;
  }
};

/**
 * Carrega configuracao de custos do localStorage
 * @returns {object} Configuracao carregada ou padrao
 */
export const loadCostConfig = () => {
  try {
    const stored = localStorage.getItem(COST_CONFIG_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Mesclar com padrao para garantir campos novos
      return {
        fuel: { ...DEFAULT_COST_CONFIG.fuel, ...parsed.fuel },
        driver: { ...DEFAULT_COST_CONFIG.driver, ...parsed.driver },
        maintenance: { ...DEFAULT_COST_CONFIG.maintenance, ...parsed.maintenance },
        tolls: { ...DEFAULT_COST_CONFIG.tolls, ...parsed.tolls },
        other: { ...DEFAULT_COST_CONFIG.other, ...parsed.other },
      };
    }
  } catch (error) {
    console.error('[CostConfig] Error loading:', error);
  }
  return { ...DEFAULT_COST_CONFIG };
};

/**
 * Reseta configuracao para valores padrao
 */
export const resetCostConfig = () => {
  try {
    localStorage.removeItem(COST_CONFIG_STORAGE_KEY);
    return true;
  } catch (error) {
    console.error('[CostConfig] Error resetting:', error);
    return false;
  }
};

// =======================================
// Formatacao
// =======================================

/**
 * Formata valor monetario em BRL
 * @param {number} value - Valor a formatar
 * @returns {string} Valor formatado
 */
export const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);
};

/**
 * Formata porcentagem
 * @param {number} value - Valor a formatar
 * @returns {string} Porcentagem formatada
 */
export const formatPercent = (value) => {
  const sign = value > 0 ? '+' : '';
  return `${sign}${Math.round(value * 10) / 10}%`;
};

export default {
  // Configuracoes
  DEFAULT_COST_CONFIG,
  FUEL_TYPES,
  VEHICLE_PROFILES,
  // Calculos
  calculateFuelCost,
  calculateDriverCost,
  calculateMaintenanceCost,
  calculateTollCost,
  calculateOtherCosts,
  calculateRouteCost,
  calculatePlanCost,
  // Comparacao
  compareCosts,
  // Persistencia
  saveCostConfig,
  loadCostConfig,
  resetCostConfig,
  // Formatacao
  formatCurrency,
  formatPercent,
};
